import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { playerPublicPath } from '@utils/publicProfileSlug';
import VideoSnapCard, { type ProfileVideoItem } from '@components/profile/VideoSnapCard';
import ProfilePhotoCollage, { type ProfileStill } from '@components/profile/ProfilePhotoCollage';

export type PublicFamilyAchievement = {
  line: string;
  sport?: string;
  skillName?: string | null;
  score?: number | null;
};

export type PublicFamilyMember = {
  name: string;
  relation: string;
  playerIdCode?: string | null;
  publicSlug?: string | null;
  photoUrl?: string | null;
  sports?: string[];
  ageCategory?: string | null;
  achievements?: PublicFamilyAchievement[];
  stills?: ProfileStill[];
  videos?: ProfileVideoItem[];
};

export type PublicFamilyBlock = {
  members?: PublicFamilyMember[];
  live?: boolean;
};

export function familyMemberHasMedia(member: PublicFamilyMember) {
  return Boolean(
    (member.videos || []).some((item) => item.videoUrl) ||
      (member.stills || []).some((item) => item.url) ||
      (member.achievements || []).some((item) => item.line)
  );
}

const PublicFamilyCollection: React.FC<{
  family?: PublicFamilyBlock | null;
  currentPlayerSlug?: string | null;
  currentPlayerIdCode?: string | null;
  reactions?: Record<string, { counts?: Record<string, number>; mine?: Record<string, boolean> }>;
}> = ({ family, currentPlayerSlug, currentPlayerIdCode, reactions }) => {
  const members = (family?.members || []).filter((member) => {
    const slug = String(currentPlayerSlug || '').trim().toLowerCase();
    if (slug && member.publicSlug) {
      return String(member.publicSlug).toLowerCase() !== slug;
    }
    if (!currentPlayerIdCode || !member.playerIdCode) return true;
    return String(member.playerIdCode).toLowerCase() !== String(currentPlayerIdCode).toLowerCase();
  });
  const participants = members.filter(familyMemberHasMedia);
  const collagePhotos = members
    .map((member) => ({ url: String(member.photoUrl || ''), alt: member.name }))
    .filter((item) => item.url);

  if (!participants.length) {
    return (
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff', px: 1.75, py: 2 }}>
        <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 15 }}>
          Family collection
        </Typography>
        <Typography variant="body2" sx={{ color: brandColors.slate, fontWeight: 600, mt: 0.75, maxWidth: 520 }}>
          Family collection shows when members have participated. Linked parents, children, and siblings appear here with
          their contest videos and stills.
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          <ProfilePhotoCollage photos={[]} playerName="Family" />
        </Box>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 15 }}>
        Family collection
      </Typography>
      {participants.map((member) => {
        const href = playerPublicPath(member.publicSlug, member.playerIdCode);
        const videos = (member.videos || []).filter((item) => item.videoUrl);
        const stills = (member.stills || []).filter((item) => item.url);
        const lines = (member.achievements || []).map((item) => item.line).filter(Boolean);
        return (
          <Box
            key={`${member.relation}-${member.publicSlug || member.name}`}
            sx={{ border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff', p: 1.25 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                src={member.photoUrl || ''}
                alt={member.name}
                sx={{ width: 40, height: 40, bgcolor: brandColors.blue, fontWeight: 800, fontSize: 15 }}
              >
                {member.name.slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                {href ? (
                  <Typography
                    component={RouterLink}
                    to={href}
                    sx={{
                      fontWeight: 800,
                      color: brandColors.navy,
                      fontSize: 14,
                      textDecoration: 'none',
                      display: 'block',
                      lineHeight: 1.25,
                    }}
                  >
                    {member.name}
                  </Typography>
                ) : (
                  <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: 14, lineHeight: 1.25 }}>
                    {member.name}
                  </Typography>
                )}
                <Typography sx={{ color: brandColors.slate, fontWeight: 700, fontSize: 12 }}>
                  {[member.relation, member.sports?.slice(0, 2).join(' · ')].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Stack>

            {lines.length ? (
              <Stack spacing={0.25} sx={{ mt: 0.9 }}>
                {lines.map((line) => (
                  <Typography key={line} sx={{ color: brandColors.navy, fontWeight: 700, fontSize: 12, lineHeight: 1.35 }}>
                    {line}
                  </Typography>
                ))}
              </Stack>
            ) : null}

            {stills.length ? (
              <Stack direction="row" spacing={0.6} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {stills.map((still) => (
                  <Box
                    key={still.url}
                    component="img"
                    src={still.url}
                    alt={still.alt || member.name}
                    sx={{
                      width: 72,
                      height: 72,
                      objectFit: 'cover',
                      borderRadius: 0.75,
                      border: '1px solid #e2e8f0',
                      display: 'block',
                    }}
                  />
                ))}
              </Stack>
            ) : null}

            {videos.length ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 148px))',
                  gap: 0.75,
                  mt: 1,
                }}
              >
                {videos.map((video, index) => (
                  <VideoSnapCard
                    key={`${video.videoKey || video.videoUrl}-${index}`}
                    video={video}
                    reaction={reactions?.[video.videoKey || '']}
                    compact={index > 1}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        );
      })}
      <ProfilePhotoCollage photos={collagePhotos} playerName="Family" />
    </Stack>
  );
};

export default PublicFamilyCollection;
